import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import qs from 'qs';
import { get, getOr, isEmpty } from 'lodash/fp';
import { Alert, Content, Panel, PanelGroup } from 'rsuite';
import { useLocation } from 'react-router-dom';
import * as PERMISSIONS from '../../../constants/permissions/reservation';
import { modifyAccess } from '../../../helpers/permission';
import { parseInt } from '../../../helpers/utils';
import { CLIENT_SEARCH } from '../../../constants/custom-list/Types';
import Panels from '../../../constants/reservation/CreatePanels';
import ReservationForm from '../../../components/reservations/Form';
import AccessDenied from '../../../components/AccessDenied';
import AccessControl from '../../../components/AccessControl';
import PageLayout from '../../../components/reservations/PageLayout';
import PageHeader from '../../../components/PageHeader';
import GuestSearch from '../../../containers/GuestSearch';
import SmtpAccountDisconnectedMessage from '../../../components/SmtpAccountDisconnectedMessage';
import { useSmtpAccounts } from '../../../hooks/useSmtpAccounts';
import { switchCreateReservationPanel } from '../../../redux/Reservation/actions/routes/create';
import { getActivePanel } from '../../../redux/Reservation/selectors/routes/create';

export default () => {
  const dispatch = useDispatch();
  const { showDisconnectedMessage } = useSmtpAccounts();
  const { search: queryString } = useLocation();
  const activePanel = useSelector(getActivePanel);
  const [guest, setGuest] = useState({});
  const [formData, setFormData] = useState({});

  useEffect(
    () => {
      const guestQuery = qs.parse(queryString, { ignoreQueryPrefix: true });
      if (!isEmpty(guestQuery)) {
        const guestId = get('guestId', guestQuery)
          ? parseInt(guestQuery.guestId)
          : null;
        const homeless = parseInt(guestQuery.homeless);
        const householdTotal = parseInt(guestQuery.householdTotal);
        setFormData({ ...guestQuery, guestId, homeless, householdTotal });

        if (get('firstName', guestQuery))
          dispatch(switchCreateReservationPanel(Panels.DETAILS));

        if (guestId) {
          setGuest({
            id: guestId,
            firstname: guestQuery.firstName,
            lastname: guestQuery.lastName,
            street_address: guestQuery.streetAddress,
            apartment: guestQuery.apartment,
            city: guestQuery.city,
            state: guestQuery.state,
            zipcode: guestQuery.zipCode,
            phone: guestQuery.homePhone,
            workphone: guestQuery.workPhone,
            cellphone: guestQuery.cellPhone,
            email: guestQuery.email,
            household_total: householdTotal,
            homeless
          });
        }
      }

      return function resetPanel() {
        dispatch(switchCreateReservationPanel(Panels.SEARCH));
      };
    },
    [queryString]
  );

  const handleSelectPanel = panelKey => {
    dispatch(switchCreateReservationPanel(panelKey));
  };

  const handleSelectGuest = value => {
    if (!value) {
      setGuest({});
      setFormData({});
    } else {
      const { blackball: isBanned } = value;
      if (isBanned) {
        Alert.error('Banned Guests cannot have a Reservation.', 5000);
        return;
      }
      setGuest(value);
      setFormData({
        guestId: value.id,
        firstName: value.firstname,
        lastName: value.lastname,
        homeless: getOr(0, 'homeless', value),
        streetAddress: getOr('', 'street_address', value),
        city: getOr('', 'city', value),
        apartment: getOr('', 'apartment', value),
        state: getOr('', 'state', value),
        zipCode: getOr('', 'zipcode', value),
        email: getOr('', 'email', value),
        homePhone: getOr('', 'phone', value),
        workPhone: getOr('', 'workphone', value),
        cellPhone: getOr('', 'cellphone', value),
        householdTotal: getOr(1, 'household_total', value)
      });
    }
    dispatch(switchCreateReservationPanel(Panels.DETAILS));
  };

  const handleBack = () => {
    handleSelectPanel(Panels.SEARCH);
  };

  return (
    <PageLayout>
      <AccessControl
        allowOnlyFor={modifyAccess(PERMISSIONS.MODULE)}
        fallback={<AccessDenied />}
      >
        <PageHeader title="Create Reservation" />
        <Content style={{ marginTop: 0 }}>
          {showDisconnectedMessage && <SmtpAccountDisconnectedMessage />}
          <PanelGroup
            activeKey={activePanel}
            onSelect={handleSelectPanel}
            accordion
            bordered
          >
            <Panel
              eventKey={Panels.SEARCH}
              header={
                <strong
                  style={{
                    color: Panels.SEARCH === activePanel ? 'inherit' : '#aaa'
                  }}
                >
                  Search Guest or Create New
                </strong>
              }
            >
              <GuestSearch
                type={CLIENT_SEARCH}
                onSelectGuest={handleSelectGuest}
              />
            </Panel>
            {activePanel === Panels.DETAILS && (
              <Panel
                eventKey={Panels.DETAILS}
                header={
                  <strong
                    style={{
                      color: Panels.DETAILS === activePanel ? 'inherit' : '#aaa'
                    }}
                  >
                    Reservation Details
                  </strong>
                }
              >
                <ReservationForm
                  data={formData}
                  guest={guest}
                  onBack={handleBack}
                />
              </Panel>
            )}
          </PanelGroup>
        </Content>
      </AccessControl>
    </PageLayout>
  );
};
